// Supply Network panel: bulk-action bar that appears whenever the user
// has at least one dealer location selected. Hosts the plant select +
// Assign / Clear-selection controls. The Assign button routes through
// the confirm modal (via the hook's `requestBulkApply`) when any
// pre-existing plant assignments would be overwritten.
//
// Structural `m` prop: both the non-app dealer-locations hook and the
// app-using subscriber-locations hook satisfy this surface, so the
// bulk bar is shared across both tabs of the Dealer-locations slider.
import type { MfgPlant } from "@/lib/manufacturingClient";
import { ghostBtnClass, inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export type DealerLocationsBulkBarBindings = {
  selectedIds: Set<string>;
  bulkPlantId: string;
  setBulkPlantId: (id: string) => void;
  plants: ReadonlyArray<MfgPlant>;
  requestBulkApply: () => void;
  clearSelection: () => void;
  actionBusy: boolean;
  isNoOp: boolean;
  pendingReassignments: ReadonlyArray<unknown>;
};

export function ManufacturingPlantDealerLocationsBulkBar({
  m,
}: {
  m: DealerLocationsBulkBarBindings;
}) {
  if (m.selectedIds.size === 0) return null;
  const selectedCount = m.selectedIds.size;
  return (
    <div className="sticky top-0 z-10 rounded-2xl border border-indigo-200 bg-indigo-50/80 px-3 py-3 shadow-sm backdrop-blur dark:border-indigo-800 dark:bg-indigo-950/60">
      <div className="flex flex-wrap items-end gap-2">
        <div className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
          {selectedCount} location{selectedCount === 1 ? "" : "s"} selected
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-indigo-900/80 dark:text-indigo-100/80">
            Assign to plant
            <select
              className={`${inputClass} mt-1`}
              value={m.bulkPlantId}
              onChange={(e) => m.setBulkPlantId(e.target.value)}
            >
              <option value="">(no plant — clear / inherit from org)</option>
              {m.plants
                .filter((p) => p.is_active)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.plant_code} · {p.name}
                  </option>
                ))}
            </select>
          </label>
        </div>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          onClick={m.requestBulkApply}
          disabled={m.actionBusy || m.isNoOp}
          title={
            m.isNoOp
              ? "Every selected location is already at this plant"
              : "Assign the selected locations to the chosen plant"
          }
        >
          {m.actionBusy ? "Applying…" : "Assign"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={m.clearSelection}
          disabled={m.actionBusy}
        >
          Clear selection
        </button>
      </div>
      {m.pendingReassignments.length > 0 && !m.isNoOp ? (
        <div className="mt-2 text-xs text-amber-800 dark:text-amber-200">
          {m.pendingReassignments.length} of the selected location
          {m.pendingReassignments.length === 1 ? " is" : "s are"} already assigned
          to a different plant — you'll see a confirmation before changes apply.
        </div>
      ) : null}
    </div>
  );
}
